package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.calendar.CalendarApp
import com.evoapps.evoshift.domain.calendar.CalendarDevice
import com.evoapps.evoshift.domain.calendar.CalendarSubscriptionLinks
import com.evoapps.evoshift.domain.model.CalendarFeed
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.calendar.ExportShiftCalendarUseCase
import com.evoapps.evoshift.domain.usecase.calendar.ManageCalendarFeedUseCase
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.settings.LocalSettings
import com.evoapps.evoshift.ui.reports.ExportFile
import com.evoapps.evoshift.ui.reports.FileSaver
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.add_to_calendar_none
import evoshift.composeapp.generated.resources.add_to_calendar_saved
import evoshift.composeapp.generated.resources.calendar_link_copied
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/** What a calendar button or "Copy link" does once the link is there (SH-202). */
sealed interface CalendarLinkTarget {
    data class App(val app: CalendarApp) : CalendarLinkTarget
    data object Copy : CalendarLinkTarget

    /** "Show the QR code" (desktop and web): only the link, for the code. */
    data object Qr : CalendarLinkTarget
}

/** A question before the link changes (SH-202): calendars added with the old link stop updating. */
sealed interface CalendarConfirm {
    /** "Create a new link" from Manage. */
    data object NewLink : CalendarConfirm

    /** "Turn off". */
    data object TurnOff : CalendarConfirm

    /** A calendar button on a device that does not know the link (made elsewhere): a new one, then [then]. */
    data class Replace(val then: CalendarLinkTarget) : CalendarConfirm
}

/** One thing the screen does for the view model: open an address or copy text. */
sealed interface CalendarHandOff {
    data class Open(val uri: String) : CalendarHandOff
    data class Copy(val text: String) : CalendarHandOff
}

/**
 * "Your shifts in your calendar" (SH-202, SH-45). [feedUrl] is the private link when this device
 * knows it (created here; the server keeps only its hash), [apps] the calendar buttons in this
 * platform's order, [handOff] the next address to open or text to copy.
 */
data class CalendarState(
    val busy: Boolean = false,
    val error: UiText? = null,
    val message: UiText? = null,
    val linkActive: Boolean = false,
    val linkLoaded: Boolean = false,
    val createdAt: String? = null,
    val feedUrl: String? = null,
    val apps: List<CalendarApp> = emptyList(),
    val showQr: Boolean = false,
    val confirm: CalendarConfirm? = null,
    val handOff: CalendarHandOff? = null
) {
    /** The link is on but was made on another device (or before SH-202): adding a calendar here replaces it. */
    val linkElsewhere: Boolean get() = linkActive && feedUrl == null

    /** The address for the QR code (webcal: an iPhone camera offers "Subscribe"). */
    val qrLink: String? get() = feedUrl?.takeIf { showQr }?.let(CalendarSubscriptionLinks::webcal)
}

/**
 * "Your shifts in your calendar" (SH-202, before SH-45's "Add to calendar"): one button per calendar
 * app opens its add-a-subscription page with the private link, creating the link on the first tap;
 * the link can be replaced or turned off. The link is remembered on this device ([LocalSettings],
 * per person and per link, so that the buttons keep working) and forgotten when the server says it
 * is off or a newer one exists. Also saves a month's published shifts as an .ics file.
 */
class CalendarViewModel(
    private val exportCalendar: ExportShiftCalendarUseCase,
    private val manageFeed: ManageCalendarFeedUseCase,
    private val fileSaver: FileSaver,
    private val getCurrentUser: GetCurrentUserUseCase? = null,
    private val localSettings: LocalSettings? = null,
    private val device: CalendarDevice = CalendarDevice.WEB
) : ViewModel() {
    private val _state = MutableStateFlow(
        CalendarState(
            apps = CalendarSubscriptionLinks.order(device),
            showQr = device == CalendarDevice.WEB || device == CalendarDevice.DESKTOP || device == CalendarDevice.MAC
        )
    )
    val state = _state.asStateFlow()

    private var uid: String? = null
    private var personName: String? = null

    /** The screen opened: whether the link is on, and the link itself when this device keeps it. */
    fun open() {
        _state.update { it.copy(error = null, message = null, confirm = null, handOff = null) }
        viewModelScope.launch {
            loadUser()
            manageFeed.status().fold(
                onSuccess = { feed ->
                    val known = remembered()?.takeIf { feed.active && it.first != null && it.first == feed.createdAt }?.second
                    if (!feed.active || known == null) forget()
                    _state.update {
                        it.copy(linkActive = feed.active, linkLoaded = true, createdAt = feed.createdAt, feedUrl = known)
                    }
                },
                onFailure = { e -> _state.update { it.copy(error = e.toUiText()) } }
            )
        }
    }

    /** A calendar button: its page with the link; the link is created first when there is none. */
    fun addTo(app: CalendarApp) = use(CalendarLinkTarget.App(app))

    /** "Copy link": for any other calendar app. */
    fun copyLink() = use(CalendarLinkTarget.Copy)

    /** "Show the QR code": the link (created when there is none) for scanning with a phone. */
    fun showQr() = use(CalendarLinkTarget.Qr)

    private fun use(target: CalendarLinkTarget) {
        val s = _state.value
        when {
            s.busy || !s.linkLoaded -> Unit
            s.feedUrl != null -> handOff(target, s.feedUrl)
            s.linkActive -> _state.update { it.copy(confirm = CalendarConfirm.Replace(target)) }
            else -> create(then = target)
        }
    }

    /** "Create a new link": asks first (calendars added with the old link stop updating). */
    fun askNewLink() = _state.update { it.copy(confirm = CalendarConfirm.NewLink) }

    /** "Turn off": asks first. */
    fun askTurnOff() = _state.update { it.copy(confirm = CalendarConfirm.TurnOff) }

    fun dismissConfirm() = _state.update { it.copy(confirm = null) }

    /** The question answered yes. */
    fun confirm() {
        val question = _state.value.confirm ?: return
        _state.update { it.copy(confirm = null) }
        when (question) {
            CalendarConfirm.NewLink -> create(then = null)
            CalendarConfirm.TurnOff -> revoke()
            is CalendarConfirm.Replace -> create(then = question.then)
        }
    }

    /** The screen opened the address or copied the text. */
    fun handedOff() {
        val done = _state.value.handOff ?: return
        _state.update {
            it.copy(handOff = null, message = if (done is CalendarHandOff.Copy) UiText.Resource(Res.string.calendar_link_copied) else it.message)
        }
    }

    fun download(rosterId: String) = run {
        val file = exportCalendar(rosterId).getOrThrow()
        if (file.events == 0) return@run UiText.Resource(Res.string.add_to_calendar_none) to false
        fileSaver.save(ExportFile(file.name, "text/calendar", file.text))
        AppTelemetry.log(AnalyticsEvent.CalendarExported)
        UiText.Resource(Res.string.add_to_calendar_saved, file.events) to true
    }

    private fun create(then: CalendarLinkTarget?) = run {
        val feed = manageFeed.create().getOrThrow()
        AppTelemetry.log(AnalyticsEvent.CalendarLinkCreated)
        keep(feed)
        _state.update { it.copy(linkActive = feed.active, linkLoaded = true, createdAt = feed.createdAt, feedUrl = feed.url) }
        val url = feed.url
        if (then != null && url != null) handOff(then, url)
        null
    }

    private fun revoke() = run {
        val feed = manageFeed.revoke().getOrThrow()
        AppTelemetry.log(AnalyticsEvent.CalendarLinkRevoked)
        forget()
        _state.update { it.copy(linkActive = feed.active, linkLoaded = true, createdAt = null, feedUrl = null) }
        null
    }

    private fun handOff(target: CalendarLinkTarget, feedUrl: String) {
        val handOff = when (target) {
            CalendarLinkTarget.Qr -> return
            is CalendarLinkTarget.App -> CalendarHandOff.Open(
                CalendarSubscriptionLinks.link(target.app, feedUrl, CalendarSubscriptionLinks.calendarName(personName))
            )
            CalendarLinkTarget.Copy -> CalendarHandOff.Copy(CalendarSubscriptionLinks.https(feedUrl))
        }
        val id = when (target) {
            is CalendarLinkTarget.App -> target.app.name.lowercase()
            CalendarLinkTarget.Copy -> "copy"
            CalendarLinkTarget.Qr -> return
        }
        AppTelemetry.log(AnalyticsEvent.CalendarAppOpened(id))
        _state.update { it.copy(handOff = handOff, error = null, message = null) }
    }

    private suspend fun loadUser() {
        if (uid != null) return
        val user = getCurrentUser?.let { current -> withTimeoutOrNull(USER_TIMEOUT_MS) { current().first() } }
        uid = user?.uid
        personName = user?.displayName
    }

    /** The link kept on this device for this person: (its createdAt, the link). */
    private fun remembered(): Pair<String?, String>? {
        val person = uid ?: return null
        val saved = runCatching { localSettings?.getString(KEY) }.getOrNull() ?: return null
        val parts = saved.split('\n')
        if (parts.size != 3 || parts[0] != person || parts[2].isBlank()) return null
        return parts[1].ifBlank { null } to parts[2]
    }

    private fun keep(feed: CalendarFeed) {
        val person = uid
        val url = feed.url
        if (person == null || url == null || feed.createdAt == null) return forget()
        runCatching { localSettings?.putString(KEY, "$person\n${feed.createdAt}\n$url") }
    }

    private fun forget() {
        runCatching { localSettings?.putString(KEY, null) }
    }

    /** Runs [block] busy; it answers a message and whether that message is good news (else shown as an error). */
    private fun run(block: suspend () -> Pair<UiText, Boolean>?) {
        if (_state.value.busy) return
        _state.update { it.copy(busy = true, error = null, message = null) }
        viewModelScope.launch {
            runCatching { block() }.fold(
                onSuccess = { answer ->
                    _state.update {
                        when {
                            answer == null -> it.copy(busy = false)
                            answer.second -> it.copy(busy = false, message = answer.first)
                            else -> it.copy(busy = false, error = answer.first)
                        }
                    }
                },
                onFailure = { e -> _state.update { it.copy(busy = false, error = e.toUiText()) } }
            )
        }
    }

    private companion object {
        /** "uid\ncreatedAt\nlink": one person's link at a time on a device. */
        const val KEY = "calendar_feed_link"
        const val USER_TIMEOUT_MS = 3_000L
    }
}
